import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CONTACT_INQUIRY_CATEGORY_LABELS,
  CONTACT_INQUIRY_STATUSES,
  CONTACT_INQUIRY_STATUS_LABELS,
  type ContactInquiryStatus,
} from '@kulum/shared';
import {
  fetchContactInquiryDetail,
  updateContactInquiry,
  type AdminContactInquiry,
} from '@/lib/inquiryService';
import { useAuthStore } from '@/stores/authStore';
import { Button, Loading } from '@/components/ui';

function formatDateTime(date?: Date | null): string {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

interface InquiryDetailPanelProps {
  inquiryId?: string;
  embedded?: boolean;
  onClose?: () => void;
  onUpdated?: () => void;
}

export function InquiryDetailPanel({
  inquiryId,
  embedded = false,
  onClose,
  onUpdated,
}: InquiryDetailPanelProps) {
  const adminProfile = useAuthStore((state) => state.adminProfile);

  const [inquiry, setInquiry] = useState<AdminContactInquiry | null>(null);
  const [status, setStatus] = useState<ContactInquiryStatus>('open');
  const [adminNote, setAdminNote] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const loadInquiry = useCallback(async () => {
    if (!inquiryId) {
      setError('問い合わせIDが不正です');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSaveMessage(null);

    try {
      const detail = await fetchContactInquiryDetail(inquiryId);

      if (!detail) {
        setError('問い合わせが見つかりません');
        setInquiry(null);
        return;
      }

      setInquiry(detail);
      setStatus(detail.status);
      setAdminNote(detail.adminNote);
    } catch (loadError) {
      console.error('Failed to fetch inquiry detail:', loadError);
      setError('問い合わせ詳細の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [inquiryId]);

  useEffect(() => {
    void loadInquiry();
  }, [loadInquiry]);

  const handleSave = async () => {
    if (!inquiry || !adminProfile?.uid) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveMessage(null);

    try {
      await updateContactInquiry({
        inquiryId: inquiry.id,
        status,
        adminNote,
        adminId: adminProfile.uid,
      });
      setSaveMessage('対応内容を保存しました');
      await loadInquiry();
      onUpdated?.();
    } catch (saveError) {
      console.error('Failed to update inquiry:', saveError);
      setError('問い合わせの更新に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Loading message="問い合わせ詳細を読み込んでいます..." />;
  }

  if (error || !inquiry) {
    return (
      <section className="space-y-4 rounded-2xl border border-red-200 bg-red-50 p-6">
        <h2 className="text-lg font-semibold text-red-800">問い合わせ詳細を表示できませんでした</h2>
        <p className="text-sm text-red-700">{error ?? 'データの取得に失敗しました'}</p>
        {embedded ? (
          <Button type="button" variant="secondary" onClick={onClose}>
            閉じる
          </Button>
        ) : (
          <Link
            to="/inquiries"
            className="inline-flex rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700"
          >
            一覧に戻る
          </Link>
        )}
      </section>
    );
  }

  return (
    <section className={embedded ? 'space-y-5 p-5' : 'space-y-5'}>
      <header className="flex items-start justify-between gap-3">
        <div>
          {embedded ? null : (
            <Link to="/inquiries" className="text-sm font-medium text-admin-accent hover:underline">
              ← 問い合わせ一覧に戻る
            </Link>
          )}
          <h2 className={embedded ? 'text-xl font-bold text-admin-ink' : 'mt-2 text-2xl font-bold text-admin-ink'}>
            問い合わせ詳細
          </h2>
        </div>
        {embedded && (
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            閉じる
          </Button>
        )}
      </header>
      <p className="text-sm text-admin-muted">
        受付日時: {formatDateTime(inquiry.createdAt)} / 最終更新: {formatDateTime(inquiry.updatedAt)}
      </p>

      {saveMessage && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {saveMessage}
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <article className="rounded-2xl border border-admin-border bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-admin-ink">問い合わせ内容</h3>
        <div className="mt-4 grid gap-3 text-sm text-admin-ink md:grid-cols-2">
          <p>
            <span className="text-admin-muted">種別:</span>{' '}
            {CONTACT_INQUIRY_CATEGORY_LABELS[inquiry.category]}
          </p>
          <p>
            <span className="text-admin-muted">状態:</span> {CONTACT_INQUIRY_STATUS_LABELS[inquiry.status]}
          </p>
          <p>
            <span className="text-admin-muted">ユーザー:</span> {inquiry.userDisplayName || '不明'}
          </p>
          <p>
            <span className="text-admin-muted">メール:</span> {inquiry.userEmail || '-'}
          </p>
          <p>
            <span className="text-admin-muted">UID:</span> {inquiry.userId || '-'}
          </p>
          <p>
            <span className="text-admin-muted">対応完了日時:</span> {formatDateTime(inquiry.resolvedAt)}
          </p>
        </div>
        <div className="mt-4 rounded-xl border border-admin-border bg-admin-bg/40 p-4">
          <p className="mb-2 text-sm font-medium text-admin-ink">本文</p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-admin-ink">
            {inquiry.message || '本文なし'}
          </p>
        </div>
      </article>

      <article className="rounded-2xl border border-admin-border bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-admin-ink">対応管理</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-admin-ink">対応ステータス</label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as ContactInquiryStatus)}
              className="w-full rounded-xl border border-admin-border px-3 py-2 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
            >
              {CONTACT_INQUIRY_STATUSES.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {CONTACT_INQUIRY_STATUS_LABELS[statusOption]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-admin-ink">最終更新者</label>
            <p className="rounded-xl border border-admin-border bg-admin-bg/40 px-3 py-2 text-sm text-admin-muted">
              {inquiry.lastUpdatedBy || '-'}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-admin-ink">管理者メモ</label>
          <textarea
            value={adminNote}
            onChange={(event) => setAdminNote(event.target.value)}
            rows={6}
            className="w-full rounded-xl border border-admin-border px-3 py-2 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
            placeholder="対応内容、追加確認事項などを記録"
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button type="button" onClick={() => void handleSave()} isLoading={isSaving}>
            保存する
          </Button>
        </div>
      </article>
    </section>
  );
}

export function InquiryDetailPage() {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  return <InquiryDetailPanel inquiryId={inquiryId} />;
}
